import { useState, useCallback, useMemo } from 'react';
import { Check, FlaskConical, HelpCircle, Award, RotateCcw } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { householdCompounds, HouseholdCompound } from '@/config/experiments/chem1131/experiment1.config';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Receiving Your Unknown Sample',
    content: `This is where your qualitative analysis skills are put to the test!

**The Challenge:**
You'll receive one of the 10 household compounds as an "unknown." Your task is to identify it using the systematic testing approach you've learned.

**Real Lab Context:**
In actual laboratories, unknown identification is a critical skill:
- Quality control testing
- Forensic analysis
- Environmental monitoring
- Medical diagnostics

**Your Strategy:**
1. Start with the solubility test (divides into 7 soluble vs 3 insoluble)
2. Follow the appropriate branch of tests
3. Use elimination to narrow down possibilities
4. Confirm with specific confirmatory tests`
  },
  2: {
    title: 'Systematic Testing Approach',
    content: `Follow the identification flowchart systematically for efficient identification.

**The Decision Tree:**

\`\`\`
Unknown Compound
      │
      ▼
  Solubility Test
      │
  ┌───┴───┐
  │       │
Soluble  Insoluble
(7)      (3)
  │       │
  ▼       ▼
Phenol.  Iodine Test
Test     │
  │    ┌─┴─┐
  │    +   −
  │  Starch CaCO₃/CaSO₄
  │         │
  │       Vinegar
  │         │
  │      ┌──┴──┐
  │      +     −
  │    CaCO₃  CaSO₄
\`\`\`

**Efficiency Tip:**
Each test should eliminate multiple possibilities. A well-chosen sequence identifies any compound in 2-4 tests.`
  },
  3: {
    title: 'Making Your Final Identification',
    content: `Confidence in identification comes from consistent test results.

**Before Submitting Your Answer:**
1. Review all test results
2. Ensure results are internally consistent
3. Check against expected properties
4. Consider if any tests should be repeated

**Common Pitfalls:**
- Rushing to conclude before sufficient testing
- Misinterpreting ambiguous results
- Forgetting to consider all possibilities

**Confirmatory Testing:**
If you've narrowed to 1-2 candidates, a final confirmatory test can provide certainty. For example:
- Suspected NaOH? Add phenolphthalein (strong pink) + vinegar (no bubbles)
- Suspected sucrose? Heat test shows caramelization

**Congratulations!**
Successfully identifying an unknown compound demonstrates mastery of qualitative analysis principles!`
  }
};

type TestType = 'solubility' | 'iodine' | 'vinegar' | 'phenolphthalein' | 'naoh' | 'alcohol' | 'hotwater';

interface TestResult {
  test: TestType;
  result: string;
  eliminates: string[];
}

export default function Part7Unknown() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [unknownReceived, setUnknownReceived] = useState(false);
  const [unknownCompound, setUnknownCompound] = useState<HouseholdCompound | null>(null);
  const [testsPerformed, setTestsPerformed] = useState<TestResult[]>([]);
  const [guess, setGuess] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const compounds = Object.values(householdCompounds);

  // Randomly select an unknown compound
  const handleReceiveUnknown = useCallback(() => {
    if (!unknownReceived) {
      const randomIndex = Math.floor(Math.random() * compounds.length);
      setUnknownCompound(compounds[randomIndex]);
      setUnknownReceived(true);
      setCurrentStep(2);
    }
  }, [unknownReceived, compounds]);

  // Perform a test
  const handleTest = useCallback((testType: TestType) => {
    if (!unknownCompound) return;

    let result = '';
    let eliminates: string[] = [];

    switch (testType) {
      case 'solubility':
        result = unknownCompound.soluble ? 'Dissolved (Soluble)' : 'Did not dissolve (Insoluble)';
        eliminates = compounds
          .filter(c => c.soluble !== unknownCompound.soluble)
          .map(c => c.id);
        break;
      case 'iodine':
        result = unknownCompound.iodinePositive ? 'Blue-black (STARCH!)' : 'Brown-purple (negative)';
        if (!unknownCompound.soluble) {
          eliminates = compounds
            .filter(c => !c.soluble && c.iodinePositive !== unknownCompound.iodinePositive)
            .map(c => c.id);
        }
        break;
      case 'vinegar':
        result = unknownCompound.carbonateReaction ? 'Bubbles (carbonate!)' : 'No bubbles';
        eliminates = compounds
          .filter(c => c.carbonateReaction !== unknownCompound.carbonateReaction)
          .map(c => c.id);
        break;
      case 'phenolphthalein':
        result = unknownCompound.basicWithPhenolphthalein ? 'Pink (BASIC)' : 'Colorless (neutral/acidic)';
        if (unknownCompound.soluble) {
          eliminates = compounds
            .filter(c => c.soluble && c.basicWithPhenolphthalein !== unknownCompound.basicWithPhenolphthalein)
            .map(c => c.id);
        }
        break;
      case 'naoh':
        result = unknownCompound.id === 'mgso4' ? 'White precipitate (Mg(OH)₂)' : 'No precipitate';
        if (unknownCompound.soluble && !unknownCompound.basicWithPhenolphthalein) {
          eliminates = unknownCompound.id === 'mgso4'
            ? compounds.filter(c => c.id !== 'mgso4' && c.soluble && !c.basicWithPhenolphthalein).map(c => c.id)
            : ['mgso4'];
        }
        break;
      case 'alcohol':
        result = unknownCompound.alcoholSoluble ? 'Dissolved in alcohol' : 'Did not dissolve';
        if (unknownCompound.soluble && !unknownCompound.basicWithPhenolphthalein) {
          eliminates = compounds
            .filter(c => c.soluble && !c.basicWithPhenolphthalein && c.alcoholSoluble !== unknownCompound.alcoholSoluble)
            .map(c => c.id);
        }
        break;
      case 'hotwater':
        result = unknownCompound.hotWaterSoluble ? 'Fully dissolved when heated' : 'Partially dissolved';
        if (unknownCompound.id === 'sucrose' || unknownCompound.id === 'nacl') {
          eliminates = unknownCompound.hotWaterSoluble ? ['nacl'] : ['sucrose'];
        }
        break;
    }

    setTestsPerformed(prev => [...prev, { test: testType, result, eliminates }]);
    if (testsPerformed.length === 0) {
      setCurrentStep(3);
    }
  }, [unknownCompound, compounds, testsPerformed.length]);

  // Submit guess
  const handleSubmitGuess = useCallback((compoundId: string) => {
    if (unknownCompound) {
      setGuess(compoundId);
      setIsCorrect(compoundId === unknownCompound.id);
    }
  }, [unknownCompound]);

  // Reset for another try
  const handleReset = useCallback(() => {
    setUnknownReceived(false);
    setUnknownCompound(null);
    setTestsPerformed([]);
    setGuess(null);
    setIsCorrect(null);
    setCurrentStep(1);
  }, []);

  // Get eliminated compounds based on tests
  const eliminatedCompounds = useMemo(() => {
    const eliminated = new Set<string>();
    testsPerformed.forEach(test => {
      test.eliminates.forEach(id => eliminated.add(id));
    });
    return eliminated;
  }, [testsPerformed]);

  // Get remaining candidates
  const remainingCandidates = useMemo(() => {
    return compounds.filter(c => !eliminatedCompounds.has(c.id));
  }, [compounds, eliminatedCompounds]);

  // Available tests based on what we know
  const availableTests = useMemo(() => {
    const performed = new Set(testsPerformed.map(t => t.test));
    const tests: { type: TestType; name: string; description: string }[] = [];

    if (!performed.has('solubility')) {
      tests.push({ type: 'solubility', name: 'Solubility Test', description: 'Add water and shake' });
    }

    // After solubility, offer appropriate tests
    if (performed.has('solubility') && unknownCompound) {
      if (!unknownCompound.soluble) {
        if (!performed.has('iodine')) {
          tests.push({ type: 'iodine', name: 'Iodine Test', description: 'Test for starch' });
        }
        if (!performed.has('vinegar')) {
          tests.push({ type: 'vinegar', name: 'Vinegar Test', description: 'Test for carbonate' });
        }
      } else {
        if (!performed.has('phenolphthalein')) {
          tests.push({ type: 'phenolphthalein', name: 'Phenolphthalein', description: 'Test for base' });
        }
        if (performed.has('phenolphthalein')) {
          if (unknownCompound.basicWithPhenolphthalein) {
            if (!performed.has('vinegar')) {
              tests.push({ type: 'vinegar', name: 'Vinegar Test', description: 'Distinguish Na₂CO₃/NaOH' });
            }
          } else {
            if (!performed.has('vinegar')) {
              tests.push({ type: 'vinegar', name: 'Vinegar Test', description: 'Test for NaHCO₃' });
            }
            if (!performed.has('naoh')) {
              tests.push({ type: 'naoh', name: 'NaOH Test', description: 'Test for MgSO₄' });
            }
            if (!performed.has('alcohol')) {
              tests.push({ type: 'alcohol', name: 'Alcohol Test', description: 'Test for boric acid' });
            }
            if (!performed.has('hotwater')) {
              tests.push({ type: 'hotwater', name: 'Hot Water Test', description: 'Distinguish NaCl/Sucrose' });
            }
          }
        }
      }
    }

    return tests;
  }, [testsPerformed, unknownCompound]);

  const completedSteps = [unknownReceived, testsPerformed.length > 0, guess !== null].filter(Boolean).length;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part VII: Unknown Identification</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Receive Unknown', hint: 'Get your mystery compound', done: unknownReceived },
            { id: 2, title: 'Perform Tests', hint: 'Systematically identify', done: testsPerformed.length > 0 },
            { id: 3, title: 'Submit ID', hint: 'Make your identification', done: guess !== null }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Test History */}
        {testsPerformed.length > 0 && (
          <div className="test-history">
            <h4>Tests Performed</h4>
            {testsPerformed.map((test, i) => (
              <div key={i} className="history-item">
                <span className="test-name">{test.test}</span>
                <span className="test-result">{test.result}</span>
              </div>
            ))}
          </div>
        )}

        {/* Remaining Candidates */}
        {unknownReceived && guess === null && (
          <div className="candidates-panel">
            <h4>Remaining Candidates ({remainingCandidates.length})</h4>
            <div className="candidates-list">
              {compounds.map(c => (
                <span
                  key={c.id}
                  className={`candidate ${eliminatedCompounds.has(c.id) ? 'eliminated' : 'active'}`}
                >
                  {c.formula}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Unknown Identification"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['All reagents', 'Flow chart', 'Test tubes']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="unknown-workspace">
          {/* Receive Unknown */}
          {!unknownReceived && (
            <div className="receive-prompt" onClick={handleReceiveUnknown}>
              <HelpCircle size={48} className="mystery-icon" />
              <h3>Mystery Compound</h3>
              <p>You will receive one of the 10 household compounds as an unknown. Use systematic testing to identify it.</p>
              <button className="receive-btn">Receive Unknown Sample</button>
            </div>
          )}

          {/* Unknown Sample Display */}
          {unknownReceived && guess === null && (
            <div className="testing-area">
              <div className="unknown-sample">
                <svg viewBox="0 0 60 100" width={80} height={130}>
                  <rect x="15" y="10" width="30" height="70" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                  <ellipse cx="30" cy="80" rx="15" ry="6" fill="rgba(200,220,240,0.4)" stroke="#7A9BB8" strokeWidth="1.5" />
                  <ellipse cx="30" cy="70" rx="12" ry="5" fill="#F5F5F5" />
                  <rect x="17" y="15" width="26" height="16" fill="#FF6B6B" stroke="#D32F2F" strokeWidth="0.5" rx="2" />
                  <text x="30" y="26" fontSize="8" fill="white" textAnchor="middle" fontWeight="bold">???</text>
                </svg>
                <span className="sample-label">Unknown Sample</span>
              </div>

              {/* Available Tests */}
              <div className="tests-section">
                <h4>Available Tests</h4>
                <div className="tests-grid">
                  {availableTests.map(test => (
                    <button
                      key={test.type}
                      className="test-button"
                      onClick={() => handleTest(test.type)}
                    >
                      <span className="test-name">{test.name}</span>
                      <span className="test-desc">{test.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Make a Guess */}
              {remainingCandidates.length <= 3 && testsPerformed.length > 0 && (
                <div className="guess-section">
                  <h4>Ready to Identify?</h4>
                  <div className="guess-buttons">
                    {remainingCandidates.map(c => (
                      <button
                        key={c.id}
                        className="guess-button"
                        onClick={() => handleSubmitGuess(c.id)}
                      >
                        {c.formula}
                        <span className="common-name">{c.commonName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Result Display */}
          {guess !== null && (
            <div className={`result-display ${isCorrect ? 'correct' : 'incorrect'}`}>
              {isCorrect ? (
                <>
                  <Award size={64} className="result-icon" />
                  <h2>Correct!</h2>
                  <p>You successfully identified the unknown compound as:</p>
                  <div className="compound-reveal">
                    <span className="formula">{unknownCompound?.formula}</span>
                    <span className="name">{unknownCompound?.name}</span>
                    <span className="common">({unknownCompound?.commonName})</span>
                  </div>
                  <p className="tests-count">Identified in {testsPerformed.length} test(s)</p>
                </>
              ) : (
                <>
                  <HelpCircle size={64} className="result-icon" />
                  <h2>Not Quite</h2>
                  <p>Your guess: <strong>{householdCompounds[guess]?.formula}</strong></p>
                  <p>The actual compound was:</p>
                  <div className="compound-reveal">
                    <span className="formula">{unknownCompound?.formula}</span>
                    <span className="name">{unknownCompound?.name}</span>
                  </div>
                </>
              )}
              <button className="reset-btn" onClick={handleReset}>
                <RotateCcw size={16} />
                Try Another Unknown
              </button>
            </div>
          )}
        </div>

        <style>{`
          .unknown-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .receive-prompt {
            background: var(--bg-elevated);
            border: 2px dashed var(--border);
            border-radius: 16px;
            padding: 40px;
            text-align: center;
            max-width: 400px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .receive-prompt:hover {
            border-color: var(--secondary);
            border-style: solid;
          }

          .mystery-icon {
            color: #F59E0B;
            margin-bottom: 16px;
          }

          .receive-prompt h3 {
            margin: 0 0 12px 0;
          }

          .receive-prompt p {
            margin: 0 0 20px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .receive-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 12px 28px;
            border-radius: 8px;
            font-weight: 600;
            font-size: 15px;
            cursor: pointer;
          }

          .testing-area {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
          }

          .unknown-sample {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            background: var(--bg-elevated);
            padding: 20px;
            border-radius: 12px;
            border: 2px solid #F59E0B;
          }

          .sample-label {
            font-weight: 600;
            color: #F59E0B;
          }

          .tests-section {
            width: 100%;
            max-width: 500px;
          }

          .tests-section h4 {
            margin: 0 0 12px 0;
            color: var(--text-secondary);
            font-size: 14px;
            text-align: center;
          }

          .tests-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .test-button {
            background: var(--bg-elevated);
            border: 1px solid var(--border);
            border-radius: 8px;
            padding: 12px;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            flex-direction: column;
            gap: 4px;
            text-align: left;
          }

          .test-button:hover {
            border-color: var(--secondary);
            background: rgba(0, 168, 107, 0.1);
          }

          .test-button .test-name {
            font-weight: 600;
            color: var(--text-primary);
            font-size: 13px;
          }

          .test-button .test-desc {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .guess-section {
            width: 100%;
            max-width: 400px;
            text-align: center;
          }

          .guess-section h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
          }

          .guess-buttons {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            justify-content: center;
          }

          .guess-button {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 12px 20px;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            font-weight: 600;
            transition: all 0.2s;
          }

          .guess-button:hover {
            transform: scale(1.05);
          }

          .guess-button .common-name {
            font-size: 10px;
            font-weight: 400;
            opacity: 0.8;
          }

          .test-history {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .test-history h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .history-item {
            display: flex;
            flex-direction: column;
            padding: 6px 0;
            border-bottom: 1px solid var(--border);
          }

          .history-item:last-child {
            border-bottom: none;
          }

          .history-item .test-name {
            font-size: 11px;
            color: var(--secondary);
            font-weight: 600;
            text-transform: capitalize;
          }

          .history-item .test-result {
            font-size: 11px;
            color: var(--text-primary);
          }

          .candidates-panel {
            background: var(--bg-surface);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .candidates-panel h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .candidates-list {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }

          .candidate {
            font-size: 10px;
            padding: 3px 6px;
            border-radius: 3px;
          }

          .candidate.active {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .candidate.eliminated {
            background: rgba(150, 150, 150, 0.2);
            color: #666;
            text-decoration: line-through;
          }

          .result-display {
            background: var(--bg-elevated);
            border-radius: 16px;
            padding: 40px;
            text-align: center;
            max-width: 400px;
          }

          .result-display.correct {
            border: 3px solid var(--secondary);
          }

          .result-display.correct .result-icon {
            color: var(--secondary);
          }

          .result-display.incorrect {
            border: 3px solid #F59E0B;
          }

          .result-display.incorrect .result-icon {
            color: #F59E0B;
          }

          .result-display h2 {
            margin: 16px 0;
          }

          .result-display p {
            margin: 8px 0;
            color: var(--text-secondary);
          }

          .compound-reveal {
            background: var(--bg-surface);
            padding: 16px;
            border-radius: 8px;
            margin: 16px 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .compound-reveal .formula {
            font-size: 24px;
            font-weight: 700;
            color: var(--text-primary);
          }

          .compound-reveal .name {
            font-size: 14px;
            color: var(--text-primary);
          }

          .compound-reveal .common {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .tests-count {
            font-size: 13px;
            color: var(--secondary);
            font-weight: 500;
          }

          .reset-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            background: var(--bg-surface);
            border: 1px solid var(--border);
            padding: 10px 20px;
            border-radius: 6px;
            cursor: pointer;
            margin-top: 16px;
            transition: all 0.2s;
          }

          .reset-btn:hover {
            border-color: var(--secondary);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp1"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
